import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const GlobalFilterContext = createContext({
  isFiltersCollapsed: true,
  setIsFiltersCollapsed: () => {},
  isSavedFiltersCollapsed: true,
  setIsSavedFiltersCollapsed: () => {},
  currentModule: '',
  editFilter: undefined,
  handleEditFilterAction: () => {},
  setNewFilterName: () => {},
  newFilterName: '',
  setHasLoadedFilterValues: () => {},
  hasLoadedFilterValues: false,
  // Added defaults for filter summary expansion state
  isFilterSummaryCollapsed: false,
  setIsFilterSummaryCollapsed: () => {},
});

export const MODULES = [
  {
    path: 'trial-management',
    name: 'TRIAL_OVERVIEW',
  },
  {
    path: 'site-health',
    name: 'SITE_HEALTH',
  },
  {
    path: 'trial-portfolio',
    name: 'PORTFOLIO_HEALTH',
  },
  {
    path: 'country-management',
    name: 'COUNTRY_OVERVIEW',
  },
];

export const useGlobalFilterContext = () => useContext(GlobalFilterContext);

function GlobalFilterProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);
  const [isSavedFiltersCollapsed, setIsSavedFiltersCollapsed] = useState(true);
  const [isFilterSummaryCollapsed, setIsFilterSummaryCollapsed] =
    useState(false);
  const [currentModule, setCurrentModule] = useState(null);
  const [newFilterName, setNewFilterName] = useState('');
  const [editFilter, setEditFilter] = useState({
    filterName: null,
    moduleName: null,
    filterValues: null,
  });
  const [isEngagementMetricsApplied, setIsEngagementMetricsApplied] =
    useState(false);

  // Function to handle editing filters
  const handleEditFilterAction = (moduleName, filterName, selectedFilter) => {
    if (moduleName !== currentModule) {
      const modulePath = MODULES.filter(
        (mdl) =>
          moduleName.toLocaleLowerCase() === mdl.name.toLocaleLowerCase(),
      )?.[0]?.path;
      navigate(`/${modulePath}`);
    }
    setIsFiltersCollapsed(false);
    setIsSavedFiltersCollapsed(true);
    setNewFilterName(filterName);
    setEditFilter({ filterName, moduleName, filterValues: selectedFilter });
  };

  // Reset state on path change
  useEffect(() => {
    setEditFilter({ filterName: null, moduleName: null, filterValues: null });
    setIsFiltersCollapsed(true);
    setIsSavedFiltersCollapsed(true);
    const module = MODULES.filter(
      (mdl) =>
        mdl.path.toLocaleLowerCase() ===
        location.pathname.split('/')[1].toLocaleLowerCase(),
    )?.[0]?.name;
    setCurrentModule(module);
    setIsFilterSummaryCollapsed(false);
    if (module === 'COUNTRY_OVERVIEW') {
      const engagementMetricsApplied =
        localStorage.getItem('ENGAGEMENT_METRICS_APPLIED') === 'true';
      setIsEngagementMetricsApplied(engagementMetricsApplied);
    } else {
      setIsEngagementMetricsApplied(false);
    }
  }, [location.pathname]);

  const contextValue = useMemo(
    () => ({
      isFiltersCollapsed,
      setIsFiltersCollapsed,
      isSavedFiltersCollapsed,
      setIsSavedFiltersCollapsed,
      currentModule,
      editFilter,
      handleEditFilterAction,
      setNewFilterName,
      newFilterName,
      setEditFilter,
      isFilterSummaryCollapsed,
      setIsFilterSummaryCollapsed,
      isEngagementMetricsApplied,
      setIsEngagementMetricsApplied,
    }),
    [
      isFiltersCollapsed,
      newFilterName,
      isSavedFiltersCollapsed,
      currentModule,
      editFilter,
      isFilterSummaryCollapsed,
      isEngagementMetricsApplied,
    ],
  );

  return (
    <GlobalFilterContext.Provider value={contextValue}>
      {children}
    </GlobalFilterContext.Provider>
  );
}

GlobalFilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GlobalFilterProvider;
